/**
 * ShareAnalyticsPanel — creator-facing view of share attribution.
 *
 * Shows:
 *   • Totals — total viewer-clicks driven by all sharers, # of unique sharers
 *   • Top-sharers table with an "Award" action per row
 *   • Per-video breakdown with each video's top 3 sharers
 *
 * Award flow: pick one of the preset badges (or none = coins-only), pick
 * a tier (10 / 30 / 50) or type a custom coin amount, tap AWARD. Backend
 * debits the creator's coin/wallet balance, credits the sharer, and
 * records the badge in `creator_badges` so it appears on the sharer's
 * public profile.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, picUrl } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Share2, Award, Coins, X, Users } from "lucide-react";

const _fmt = (n) => (Number(n) || 0).toLocaleString();

function AwardDialog({ sharer, presets, onClose, onAwarded }) {
  const [badgeKey, setBadgeKey] = useState(null);
  const [coins, setCoins] = useState(10);
  const [customCoins, setCustomCoins] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const finalCoins =
      customCoins !== "" ? parseInt(customCoins, 10) : coins;
    if (!finalCoins || finalCoins < 1) {
      toast.error("Enter a coin amount");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/me/share-analytics/award", {
        sharer_user_id: sharer.sharer_user_id,
        coins: finalCoins,
        badge_key: badgeKey || null,
      });
      toast.success(
        badgeKey
          ? `${finalCoins} coins + badge sent to @${sharer.username}`
          : `${finalCoins} coins sent to @${sharer.username}`,
      );
      onAwarded?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Award failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        data-testid="award-dialog"
        className="w-full max-w-md bg-[#0A0A0C] border border-[#39FF14]/40 shadow-[0_0_28px_rgba(57,255,20,0.2)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#39FF14]">
            // reward.sharer
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="award-close"
            className="text-white/50 hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center gap-3">
            <img
              src={picUrl(sharer.picture)}
              alt={sharer.username}
              className="h-12 w-12 rounded-full object-cover border border-white/10"
            />
            <div className="min-w-0 flex-1">
              <p className="text-white font-bold">@{sharer.username}</p>
              <p className="text-white/50 text-xs font-mono">
                {_fmt(sharer.visits)} clicks · {sharer.videos_shared} of your videos
              </p>
            </div>
          </div>

          {/* Range guardrail — makes it unmistakable which window these
              numbers came from so the creator doesn't accidentally award
              lifetime hustle when they meant "reward this week". Also
              inlines the trend arrow so gainers vs fallers pop. */}
          <div
            className="border border-[#39FF14]/25 bg-[#39FF14]/[0.03] px-3 py-2.5"
            data-testid="award-range-guardrail"
          >
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#39FF14]">
              rewarding activity from · {sharer.__rangeLabel || "lifetime"}
            </p>
            <div className="mt-1.5 flex items-baseline gap-3 flex-wrap">
              <p className="text-white font-black text-lg leading-none">
                {_fmt(sharer.visits)}
                <span className="text-white/40 text-xs font-mono font-normal ml-1">
                  clicks
                </span>
              </p>
              <p className="text-white/60 text-xs">
                {sharer.videos_shared} video{sharer.videos_shared === 1 ? "" : "s"}
                {" · "}
                {_fmt(sharer.unique_viewers)} unique
              </p>
              {sharer.trend_direction && (
                <span
                  className={`font-mono text-[10px] font-bold ml-auto ${
                    sharer.trend_direction === "up"   ? "text-[#39FF14]" :
                    sharer.trend_direction === "down" ? "text-[#FF3B30]" :
                                                        "text-white/40"
                  }`}
                >
                  {sharer.trend_direction === "up" ? "↑" :
                   sharer.trend_direction === "down" ? "↓" : "–"}
                  {sharer.trend_delta_pct != null && sharer.trend_delta_pct !== 0 && (
                    <span className="ml-0.5">
                      {Math.abs(sharer.trend_delta_pct) >= 1000
                        ? "999%+"
                        : `${Math.abs(Math.round(sharer.trend_delta_pct))}%`}
                    </span>
                  )}
                  {sharer.prior_visits != null && (
                    <span className="ml-1 text-white/40 font-normal">
                      vs {_fmt(sharer.prior_visits)} prior
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Badge picker */}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
              badge (optional)
            </p>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setBadgeKey(null)}
                data-testid="badge-pick-none"
                className={`p-2 border text-center transition-all ${
                  !badgeKey
                    ? "border-white text-white bg-white/5"
                    : "border-white/10 text-white/50 hover:border-white/30"
                }`}
              >
                <span className="block text-lg leading-none mb-1">–</span>
                <span className="text-[9px] font-mono uppercase tracking-widest">
                  none
                </span>
              </button>
              {presets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setBadgeKey(p.key)}
                  data-testid={`badge-pick-${p.key}`}
                  style={
                    badgeKey === p.key
                      ? { borderColor: p.color, boxShadow: `0 0 12px ${p.color}66` }
                      : {}
                  }
                  className={`p-2 border text-center transition-all ${
                    badgeKey === p.key
                      ? "bg-white/5"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <span className="block text-lg leading-none mb-1">{p.icon}</span>
                  <span
                    className="text-[9px] font-mono uppercase tracking-widest"
                    style={badgeKey === p.key ? { color: p.color } : { color: "rgba(255,255,255,0.7)" }}
                  >
                    {p.title}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Coin tier + custom */}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
              coins
            </p>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {[10, 30, 50].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`tier-${n}`}
                  onClick={() => { setCoins(n); setCustomCoins(""); }}
                  className={`py-2 border font-mono text-sm transition-all ${
                    coins === n && customCoins === ""
                      ? "border-[#FF9500] text-[#FF9500] bg-[#FF9500]/10"
                      : "border-white/10 text-white/70 hover:border-white/30"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <input
              type="number"
              min="1"
              placeholder="or type a custom amount"
              value={customCoins}
              data-testid="custom-coins"
              onChange={(e) => setCustomCoins(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full bg-black border border-white/10 focus:border-[#FF9500] outline-none px-3 py-2 font-mono text-sm text-white"
            />
          </div>

          <button
            type="button"
            data-testid="award-submit"
            onClick={submit}
            disabled={submitting}
            className="w-full py-3 bg-[#39FF14] text-black font-mono text-xs uppercase tracking-widest font-bold hover:brightness-110 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin inline" />
            ) : (
              <>
                <Coins className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
                Award{" "}
                {customCoins !== "" ? customCoins || "?" : coins} coins
                {badgeKey ? " + badge" : ""}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ShareAnalyticsPanel({ rangeDays = 0 }) {
  const [data, setData] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [awardingSharer, setAwardingSharer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = rangeDays
        ? `/me/share-analytics?range_days=${rangeDays}`
        : "/me/share-analytics";
      const [a, p] = await Promise.all([
        api.get(url),
        api.get("/badges/presets"),
      ]);
      setData(a.data);
      setPresets(p.data || []);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [rangeDays]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="h-6 w-6 text-[#39FF14] animate-spin" />
      </div>
    );
  }
  if (!data || data.totals.unique_sharers === 0) {
    return (
      <div className="border border-white/10 bg-[#0A0A0C] p-8 text-center" data-testid="share-analytics-empty">
        <Share2 className="h-8 w-8 text-[#39FF14] mx-auto mb-3" strokeWidth={1.5} />
        <p className="font-display font-bold text-lg">No sharers yet</p>
        <p className="text-white/50 text-sm mt-1 max-w-md mx-auto">
          Once viewers share your videos, they&apos;ll show up here. Award your
          top curators with coins and a badge to lock in loyalty.
        </p>
      </div>
    );
  }

  const rangeLabel = rangeDays ? `${rangeDays}d` : "lifetime";

  return (
    <div className="space-y-6" data-testid="share-analytics-panel">
      {/* Totals row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="border border-[#39FF14]/25 bg-[#0A0A0C] p-4">
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
            total clicks driven · <span className="text-[#39FF14]">{rangeLabel}</span>
          </p>
          <p
            className="font-display font-black text-3xl text-[#39FF14] mt-1"
            data-testid="share-total-visits"
          >
            {_fmt(data.totals.total_share_visits)}
          </p>
        </div>
        <div className="border border-white/10 bg-[#0A0A0C] p-4">
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
            unique sharers · <span className="text-[#39FF14]">{rangeLabel}</span>
          </p>
          <p className="font-display font-black text-3xl text-white mt-1">
            {_fmt(data.totals.unique_sharers)}
          </p>
        </div>
        <div className="border border-white/10 bg-[#0A0A0C] p-4">
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
            videos shared · <span className="text-[#39FF14]">{rangeLabel}</span>
          </p>
          <p className="font-display font-black text-3xl text-white mt-1">
            {_fmt(data.totals.videos_shared_count)}
          </p>
        </div>
      </div>

      {/* Top sharers list */}
      <div className="border border-white/10 bg-[#0A0A0C]">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
            // top.sharers
          </p>
          <p className="font-mono text-[10px] text-white/40">
            <Users className="inline h-3 w-3 mr-1" /> {data.top_sharers.length}
          </p>
        </div>
        <div className="divide-y divide-white/5">
          {data.top_sharers.map((s) => (
            <div
              key={s.sharer_user_id}
              data-testid={`top-sharer-row-${s.rank}`}
              className="flex items-center gap-3 p-3 hover:bg-white/[0.02]"
            >
              <span className="font-mono text-white/40 text-xs w-6 shrink-0 text-center">
                #{s.rank}
              </span>
              {/* Trend arrow — only rendered when a range window is set,
                  compares this window vs the immediately-previous window
                  of the same length. Green up-arrow for gainers, red
                  down-arrow for fallers, grey dash for flat. Hidden on
                  lifetime view where the comparison doesn't apply. */}
              {s.trend_direction && (
                <span
                  data-testid={`trend-${s.rank}`}
                  className={`font-mono text-[10px] font-bold shrink-0 w-8 text-center ${
                    s.trend_direction === "up"   ? "text-[#39FF14]" :
                    s.trend_direction === "down" ? "text-[#FF3B30]" :
                                                    "text-white/30"
                  }`}
                  title={
                    s.prior_visits != null
                      ? `${s.visits} now vs ${s.prior_visits} prior · ${
                          s.trend_delta_pct != null && s.trend_delta_pct !== 0
                            ? (s.trend_delta_pct > 0 ? "+" : "") + s.trend_delta_pct + "%"
                            : "no change"
                        }`
                      : ""
                  }
                >
                  {s.trend_direction === "up" ? "↑" :
                   s.trend_direction === "down" ? "↓" : "–"}
                  {s.trend_delta_pct != null && s.trend_delta_pct !== 0 && (
                    <span className="ml-0.5">
                      {Math.abs(s.trend_delta_pct) >= 1000
                        ? "999%+"
                        : `${Math.abs(Math.round(s.trend_delta_pct))}%`}
                    </span>
                  )}
                </span>
              )}
              <img
                src={picUrl(s.picture)}
                alt={s.username}
                className="h-9 w-9 rounded-full object-cover border border-white/10 shrink-0"
              />
              <Link
                to={`/u/${s.username}`}
                className="flex-1 min-w-0 hover:underline"
              >
                <p className="text-white text-sm font-bold truncate">
                  @{s.username}
                </p>
                <p className="text-white/40 text-[10px] font-mono">
                  {_fmt(s.visits)} clicks · {s.videos_shared} vids
                  {s.already_awarded && (
                    <span className="ml-2 text-[#39FF14]">✓ awarded {s.last_award_coins}c</span>
                  )}
                </p>
              </Link>
              <button
                type="button"
                data-testid={`award-btn-${s.rank}`}
                onClick={() => setAwardingSharer({ ...s, __rangeLabel: rangeLabel })}
                className="px-3 py-1.5 border border-[#39FF14]/50 text-[#39FF14] font-mono text-[10px] uppercase tracking-widest hover:bg-[#39FF14]/10"
              >
                <Award className="inline h-3 w-3 mr-1 -mt-0.5" />
                Award
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Per-video breakdown */}
      {data.videos.filter((v) => v.sharer_visits_total > 0).length > 0 && (
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
            // per.video
          </p>
          <div className="space-y-2">
            {data.videos
              .filter((v) => v.sharer_visits_total > 0)
              .map((v) => (
                <div
                  key={v.video_id}
                  className="border border-white/10 bg-[#0A0A0C] p-3 flex items-center gap-3"
                  data-testid={`video-share-row-${v.video_id}`}
                >
                  <Link to={`/v/${v.video_id}`} className="shrink-0">
                    <img
                      src={picUrl(v.poster_url)}
                      alt=""
                      className="h-14 w-10 object-cover border border-white/10"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">
                      {v.caption || <span className="text-white/40">(no caption)</span>}
                    </p>
                    <p className="text-white/40 text-[10px] font-mono">
                      {_fmt(v.sharer_visits_total)} clicks from{" "}
                      {v.unique_sharers} sharer{v.unique_sharers === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="flex -space-x-1.5 shrink-0">
                    {v.top_sharers.slice(0, 3).map((t) => (
                      <img
                        key={t.sharer_user_id}
                        src={picUrl(t.picture)}
                        alt={t.username}
                        title={`@${t.username} · ${_fmt(t.visits)} clicks`}
                        className="h-6 w-6 rounded-full border border-black object-cover"
                      />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {awardingSharer && (
        <AwardDialog
          sharer={awardingSharer}
          presets={presets}
          onClose={() => setAwardingSharer(null)}
          onAwarded={() => load()}
        />
      )}
    </div>
  );
}
